import dayjs from "dayjs";
import type { Point } from "highcharts";
import { renderToString } from "react-dom/server";

import {
    shortMonthAndYear,
    shortMonthDayAndYearWithDayOfWeek,
    tooltipContainerConfig,
} from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import type { granularityItem } from "app/components/widget/widget-utilities/time-granularity";
import { i18nFilter } from "app/filters/i18nFilter";
import { getChange } from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/CommonChartTooltip";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { PeriodOverPeriodTooltip } from "../components/PeriodOverPeriodTooltip";
import type { SingleMetricConfiguration } from "../config/config";

// Format snapshot timestamps in UTC so the same date renders in every timezone (DRI-10166).
const weeklyRangeFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
});

const formatTooltipTitle = (timestamp: number, granularity: granularityItem, periodEnd?: number): string => {
    const date = dayjs.utc(timestamp);

    if (granularity.value === GranularityNames.WEEKLY && periodEnd) {
        return weeklyRangeFormatter.formatRange(new Date(timestamp), new Date(periodEnd));
    }

    if (granularity.value === GranularityNames.MONTHLY) {
        return date.startOf("month").format(shortMonthAndYear);
    }

    return date.format(shortMonthDayAndYearWithDayOfWeek[0]);
};

interface ISinglePeriodTooltipRow {
    webSource: { name: string; color: string };
    value: string;
    change: string | undefined;
}

// Helper function to get column definitions for single period tooltip
const getSinglePeriodColumnDefinition = (
    metricTitle: string,
    i18n: (key: string) => string,
): ColumnDefinition<ISinglePeriodTooltipRow>[] => [
    {
        align: "left",
        key: "webSource",
        size: "2fr",
        title: i18n("common.web.source"),
        selector: "webSource" as const,
        render: ({ webSource }: { webSource: { name: string; color: string } }) => {
            return (
                <ChartTooltip.LabelCell
                    label={webSource.name}
                    backgroundColor={webSource.color}
                />
            );
        },
    },
    {
        align: "right",
        key: "value",
        size: "1fr",
        title: metricTitle,
        selector: "value" as const,
        render: ({ value }: { value: string }) => <ChartTooltip.ValueCell value={value} />,
    },
    {
        align: "right",
        key: "change",
        size: "1fr",
        title: i18n("common.tooltip.change"),
        selector: "change" as const,
        render: ({ change }: { change?: string }) =>
            change ? (
                <ChartTooltip.ChangeCell
                    value={change}
                    isDecrease={Number(change.replace(/[^0-9.-]/g, "")) < 0}
                    unsigned={change === "0"}
                />
            ) : (
                <ChartTooltip.ValueCell value="—" />
            ),
    },
];

// Helper function to create period-over-period tooltip
const createPeriodOverPeriodTooltip = (points: Point[], tooltipFormatter: (value: number) => string) => {
    if (!points || !Array.isArray(points) || points.length < 2) {
        return "";
    }

    const [previousPoint, currentPoint] = points;
    const change = previousPoint.y !== 0 ? currentPoint.y / previousPoint.y - 1 : 0;

    return renderToString(
        <PeriodOverPeriodTooltip
            //@ts-expect-error originalTimestamp is defined but missing in default HighCharts types
            previousPeriodTimeStamp={previousPoint.options.originalTimestamp as number}
            currentPeriodTimeStamp={currentPoint.x}
            previousPeriodValue={previousPoint.y}
            currentPeriodValue={currentPoint.y}
            change={change}
            valueFormatter={tooltipFormatter}
        />,
    );
};

// Helper function to create single period tooltip
const createSinglePeriodTooltip = (
    points: Point[],
    tooltipFormatter: (value: number) => string,
    filteredAreaGraphData: any[],
    selectedMetric: SingleMetricConfiguration,
    timestamp: number,
    is28d: boolean,
    timeGranularity: granularityItem,
) => {
    if (points.length === 0) {
        return "";
    }

    const i18n = i18nFilter();
    const metricTitle = selectedMetric.getTitle({ is28d });

    const options = points[0].options as Record<string, unknown>;
    const periodEnd = typeof options?.periodEnd === "number" ? options.periodEnd : undefined;
    const title = formatTooltipTitle(timestamp, timeGranularity, periodEnd);

    // Create tooltip data
    const data = points.map((point) => {
        const pointIndex = point.index;
        const series = point.series;

        // Get previous data point for change calculation
        const seriesData = filteredAreaGraphData.find((s) => s.name === series.name);
        const previousDataPoint =
            pointIndex === 0 || !seriesData ? point.y : seriesData.data[pointIndex - 1]?.y || point.y;

        const change = previousDataPoint !== 0 ? point.y / previousDataPoint - 1 : 0;
        const changeFormatted =
            previousDataPoint !== 0 && previousDataPoint !== point.y
                ? (getChange(previousDataPoint, change) as string)
                : undefined;

        return {
            webSource: {
                name: series.name,
                color: series.color as string,
            },
            value: tooltipFormatter(point.y),
            change: changeFormatted,
        };
    });

    // Add total row if we have multiple series and metric supports it
    if (points.length > 1 && (selectedMetric.id === "monthly-visits" || selectedMetric.id === "unique-visitors")) {
        const totalValue = points.reduce((sum, point) => sum + point.y, 0);
        data.push({
            webSource: {
                name: i18n("websources.total"),
                color: null,
            },
            value: tooltipFormatter(totalValue),
            change: undefined, // No change for total
        });
    }

    return renderToString(
        <ChartTooltip
            width={260}
            title={title}
            data={data}
            columnDefinition={getSinglePeriodColumnDefinition(metricTitle, i18n)}
        />,
    );
};

// Main tooltip configuration function
export const createTooltipConfig = (
    isPeriodOverPeriod: boolean,
    _queryParams: any,
    filteredAreaGraphData: any[],
    selectedMetric: SingleMetricConfiguration,
    tooltipFormatter: (value: number) => string,
    is28d: boolean,
    timeGranularity: granularityItem,
): Highcharts.TooltipOptions => ({
    ...tooltipContainerConfig().tooltip,
    shared: true,
    outside: false,
    useHTML: true,
    formatter: function () {
        if (isPeriodOverPeriod) {
            return createPeriodOverPeriodTooltip(this.points, tooltipFormatter);
        } else {
            return createSinglePeriodTooltip(
                this.points,
                tooltipFormatter,
                filteredAreaGraphData,
                selectedMetric,
                this.x,
                is28d,
                timeGranularity,
            );
        }
    },
});
